import { useState, type RefObject } from 'react';
import { useBag, useUseItem, type BagItemDto } from '../api/bag';
import { useCareAction, type CareAction } from '../api/care';
import type { CompanionDto } from '../api/companions';
import { ApiClientError } from '../api/client';
import { BottomSheet, Dialog } from '../components/BottomSheet';
import { itemIconPath } from '../compositor/assetPaths';

export type FeedSheetProps = {
  companion: CompanionDto;
  onClose: () => void;
  /** Fired once a tonic use actually succeeds, with the server's new stage — HabitatScreen's
   *  CompanionCard owns the celebratory toast + wardrobe link (SCREENS.md /habitat "stage-up"),
   *  same split BagScreen/ItemDetailSheet use for their own `onActionSuccess`. This sheet closes
   *  itself right after (see `handleTonicRow` below) — a stage-up is worth surfacing on the card
   *  itself, not buried behind a still-open sheet. */
  onStageUp: (stage: string) => void;
  /** The button that opened this sheet — see BottomSheet.tsx's own doc comment on why this is
   *  needed for reliable focus-return. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
};

type SectionKey = 'food' | 'grooming' | 'toy' | 'tonic';

const SECTIONS: { key: SectionKey; title: string; action?: CareAction; buttonLabel?: string; pastTense?: string }[] = [
  { key: 'food', title: 'Food', action: 'feed', buttonLabel: 'Feed', pastTense: 'Fed' },
  { key: 'grooming', title: 'Grooming', action: 'groom', buttonLabel: 'Groom', pastTense: 'Groomed' },
  { key: 'toy', title: 'Toys', action: 'play', buttonLabel: 'Play', pastTense: 'Played' },
  { key: 'tonic', title: 'Tonics' },
];

// Server rejection codes (GlobalExceptionHandler.java) mapped to friendly copy — same pattern
// ItemDetailSheet/WardrobeScreen already use. `wrong_stage` gets the exact copy task-17-brief.md
// specifies for a tonic used at the wrong stage: honest and data-driven (the server, which alone
// knows each tonic's required stage, is the one rejecting it — this client never hardcodes which
// tonic code needs which stage, §12.1).
export const ERROR_MESSAGES: Record<string, string> = {
  not_owned: "You don't own that anymore",
  care_item_wrong_kind: "That item can't be used for this care action",
  play_not_ready: "This companion isn't ready to play again yet",
  wrong_stage: 'Not the right stage for this tonic yet.',
  item_not_usable: "That item can't be used that way",
  battle_only: 'That tonic only works in battle',
};

export function careItemError(err: unknown): string {
  if (err instanceof ApiClientError && ERROR_MESSAGES[err.code]) return ERROR_MESSAGES[err.code];
  return 'Something went wrong — please try again.';
}

const EMPTY_COPY = 'Nothing to feed, groom, or play with yet — the Thatchwell market restocks all day.';

/**
 * SCREENS.md /habitat "feed-sheet": bag consumables grouped Food/Grooming/Toys/Tonics (empty
 * sections skipped), each row tapping straight into `useCareAction` (food/grooming/toy) or
 * `useUseItem` (tonics) against `companion`. Feed/groom/play rows stay open on success (a player
 * often wants to feed AND groom in one visit) and show a brief toast; a successful tonic instead
 * closes the sheet — `onStageUp` hands the celebration to the card itself.
 *
 * Tonic rows are ALWAYS enabled (never gated by a hardcoded item-code -> stage map, §12.1) — the
 * server alone knows which stage a given tonic needs, and rejects with `wrong_stage` when it's
 * wrong. That is surfaced verbatim via `ERROR_MESSAGES` above rather than guessed client-side.
 *
 * Built on `BottomSheet` (`@radix-ui/react-dialog` — FABLE5_BUILD_PROMPT.md §3).
 */
export function FeedSheet({ companion, onClose, onStageUp, restoreFocusRef, portalContainer }: FeedSheetProps) {
  const bag = useBag();
  const careAction = useCareAction(companion.id);
  const useItem = useUseItem();

  const [toast, setToast] = useState<string | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  const items = bag.data?.items ?? [];
  const rowsFor = (key: SectionKey): BagItemDto[] => items.filter((i) => i.usable && i.subcategory === key);
  const hasAnyRows = SECTIONS.some((s) => rowsFor(s.key).length > 0);

  async function handleCareRow(item: BagItemDto, action: CareAction, pastTense: string) {
    if (pendingCode) return;
    setPendingCode(item.code);
    try {
      await careAction.mutateAsync({ action, itemCode: item.code });
      setToast(`${pastTense} — ${item.name}`);
    } catch (err) {
      setToast(careItemError(err));
    } finally {
      setPendingCode(null);
    }
  }

  async function handleTonicRow(item: BagItemDto) {
    if (pendingCode) return;
    setPendingCode(item.code);
    try {
      const res = await useItem.mutateAsync({ itemCode: item.code, companionId: companion.id });
      onStageUp(res.result.stage);
      onClose();
    } catch (err) {
      setToast(careItemError(err));
      setPendingCode(null);
    }
  }

  return (
    <BottomSheet
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="feed-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
    >
      <div className="sheet-header">
        <Dialog.Title asChild>
          <h2>Feed {companion.name}</h2>
        </Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close feed sheet">
            Close
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Food, grooming, toys, and tonics from your bag for {companion.name}.
      </Dialog.Description>

      <div className="sheet-body">
        {bag.isLoading && <p className="form-note">Loading your bag…</p>}
        {/* Fix round 1 (review finding, medium): a genuine fetch failure must never render the
            same copy as a truly empty bag — that's dishonest (§11.11 "never blank, always
            honest"). Same `.retry-card` + "Try again" → refetch shape BagScreen/HabitatScreen
            already use for this exact distinction, checked BEFORE the empty-bag branch below so
            an errored query (whose `.data` is undefined, so `hasAnyRows` would also be false)
            never falls through to the empty copy instead. */}
        {bag.isError && (
          <div className="retry-card">
            <p>Couldn&rsquo;t reach Project Pets.</p>
            <button type="button" className="btn-primary" onClick={() => bag.refetch()}>Try again</button>
          </div>
        )}
        {!bag.isLoading && !bag.isError && !hasAnyRows && <p className="form-note">{EMPTY_COPY}</p>}

        {SECTIONS.map((section) => {
          const rows = rowsFor(section.key);
          if (rows.length === 0) return null;
          const isTonic = section.key === 'tonic';
          return (
            <section key={section.key} className="feed-sheet-section">
              <h3>{section.title}</h3>
              <ul className="feed-sheet-list">
                {rows.map((item) => {
                  const busy = pendingCode === item.code;
                  const label = isTonic ? `Use ${item.name}` : (section.buttonLabel as string);
                  return (
                    <li key={item.code} className="feed-sheet-row">
                      <img src={itemIconPath(item.code)} alt="" className="feed-sheet-icon" />
                      <div className="feed-sheet-info">
                        <span className="feed-sheet-name">{item.name}</span>
                        <span className="feed-sheet-qty">Qty: {item.qty}</span>
                        {/* The server owns actual effect values (§12.1) — this shows the item's
                            own description, never an invented "+N" number. */}
                        {item.description && <p className="feed-sheet-hint">{item.description}</p>}
                      </div>
                      <button
                        type="button"
                        className="btn-quiet"
                        disabled={busy}
                        onClick={() => (isTonic
                          ? handleTonicRow(item)
                          : handleCareRow(item, section.action as CareAction, section.pastTense as string))}
                      >
                        {busy ? 'Working…' : label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}

        {toast && (
          <div className="toast" role="alert">
            <span>{toast}</span>
            <button type="button" className="toast-dismiss" onClick={() => setToast(null)}>Dismiss</button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
